// src/encounter/structured-note/objective-vitals.tsx
//
// D-74 (design desk, 6 Oct 2026; PROD-3040 batch 1, PROD-3045 N2) · frame v2 ONLY. The Objective section
// always holds the Vitals block for the physician and the nurse. Vitals are an encounter RECORD block
// (authority rule 1), never a template question. There is exactly ONE block:
//   · at the first `vitals-block` marker inside Objective (the PE step), drawn by ExamBody, or
//   · when Objective carries no marker, FIRST in Objective, drawn by `ObjectiveVitals` below.
// A marker outside Objective (an HPI marker) is not drawn in frame v2: the block lives in Objective.
// States (D-45 + the D-74 notes of 6 Oct):
//   · ready with readings  → the tiles;
//   · ready, read and empty → the tiles stay, each with an em dash (D-36), and the header's action reads
//     "Add" (the page's resolver: the same Edit dialog and save path the pre-visit vitals use);
//   · loading → Skeleton; not read (no visit ids, no resolver) → "Vitals information unavailable";
//   · failed → one line + Retry (only with a handler), no dashes, no reviewed chip, no Historical; Add/Edit kept.
// After a provider signature it is read-only (rule 38): no Add / Edit and no Mark reviewed; Retry and
// Historical stay (they only read).
//
// Found live on dev (build 97558a3): Objective held only Documents for the physician AND the nurse, so a
// nurse could not record vitals in the note at all — her main intake job.
import type { ReactNode } from 'react';
import {
  Button,
  NoteBlock,
  Skeleton,
  VitalsBlock,
  type QItem,
} from '@scripsteam/scrips-signal-ds';
import { frozenVitals, latestVitalsLabel, OBJECTIVE_VITALS_MARKER, OBJECTIVE_VITALS_TITLE, type ResolveVitals, type VitalsReadState } from './objective-vitals-model';

const QUIET = 'm-0 font-sans text-sm text-text-secondary';

/**
 * The block's body in every D-45 state. `titled`: the pills header names the block ("Vitals", inside the PE
 * block at a marker); untitled: a NoteBlock already names it, so the header keeps only its actions.
 */
export function VitalsRecordBody({
  resolve,
  marker,
  state = { status: 'ready' },
  frozen,
  note,
  titled,
}: {
  resolve?: ResolveVitals;
  marker: QItem;
  state?: VitalsReadState;
  frozen: boolean;
  note?: ReactNode;
  titled: boolean;
}) {
  const header = titled ? {} : { title: '', 'aria-label': 'Vitals readings' };
  if (state.status === 'loading') {
    return (
      <div role="status" aria-label="Loading vitals" className="flex flex-col gap-2" data-testid="vitals-loading">
        <Skeleton className="h-6 w-full" />
      </div>
    );
  }
  const resolved = state.status === 'unavailable' ? undefined : resolve?.(marker);
  if (!resolved) {
    return (
      <p className={QUIET} data-testid="vitals-unavailable">
        Vitals information unavailable
      </p>
    );
  }
  if (state.status === 'failed') {
    const failed = frozenVitals({ ...resolved, items: [], reviewed: false, onVitalsReviewed: undefined, onHistorical: undefined }, frozen);
    return (
      <VitalsBlock
        {...failed}
        {...header}
        labelTone="secondary"
        unitSpace="thin"
        note={
          <span className="flex items-center gap-2" data-testid="vitals-failed">
            <span className={QUIET}>Couldn’t load vitals.</span>
            {state.onRetry && (
              <Button variant="tertiary" size="sm" onClick={state.onRetry}>
                Retry
              </Button>
            )}
          </span>
        }
      />
    );
  }
  return <VitalsBlock {...frozenVitals(resolved, frozen)} {...header} labelTone="secondary" unitSpace="thin" {...(note ? { note } : {})} />;
}

/** The frame-drawn Vitals block, first in Objective (no marker in Objective). */
export function ObjectiveVitals({
  resolve,
  state,
  frozen,
  note,
}: {
  /** Absent (not wired) → D-45 "Vitals information unavailable". */
  resolve?: ResolveVitals;
  state?: VitalsReadState;
  /** A provider signature (records frozen): read-only. */
  frozen: boolean;
  /** The quiet "not part of an amendment" line while a signed note is amended. */
  note?: ReactNode;
}) {
  const latestLabel = latestVitalsLabel(state?.latestAt);
  return (
    <NoteBlock
      id="note-block-vitals"
      title={OBJECTIVE_VITALS_TITLE}
      titleTone="text-primary"
      border="card"
      labelTone="secondary"
      // No author / state label on the blocks (main #646, PROD-2743): the note's sign state lives in the header.
      icon="activity"
      placement="wayfinding"
      actions={latestLabel ? <span className="text-xs font-normal text-text-secondary">{latestLabel}</span> : undefined}
    >
      <div className="flex flex-col gap-4" data-objective-vitals={state?.status ?? 'ready'}>
        <VitalsRecordBody resolve={resolve} marker={OBJECTIVE_VITALS_MARKER} state={state} frozen={frozen} note={note} titled={false} />
      </div>
    </NoteBlock>
  );
}
